import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Heading } from '../ui/heading';
import { Text } from '../ui/text';
import { Skeleton } from '../components/PageChrome';
import { api, type PublicTeamSummary } from '../lib/api';

interface PublicTeamsResponse {
  teams: PublicTeamSummary[];
}

function BroadcastPreview() {
  return (
    <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-2xl border border-white/10 bg-zinc-950/85 p-4 shadow-2xl shadow-emerald-950/30 backdrop-blur sm:inset-x-auto sm:right-6 sm:w-80">
      <div className="flex items-center justify-between text-xs font-medium text-zinc-400">
        <span>RA Legends 10u · Field 3</span>
        <span className="rounded-full bg-red-500/15 px-2.5 py-1 text-red-200 ring-1 ring-red-400/20">LIVE</span>
      </div>
      <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div>
          <div className="text-sm text-zinc-400">Home</div>
          <div className="mt-1 text-3xl font-semibold text-white">4</div>
        </div>
        <div className="rounded border border-white/10 px-3 py-2 text-center">
          <div className="text-xs text-zinc-500">BOT 3</div>
          <div className="mt-1 text-sm text-zinc-200">1 out</div>
        </div>
        <div className="text-right">
          <div className="text-sm text-zinc-400">Away</div>
          <div className="mt-1 text-3xl font-semibold text-white">2</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs text-zinc-400">
        <div className="rounded bg-white/5 py-2">
          <span className="block text-zinc-100">2-1</span>
          Count
        </div>
        <div className="rounded bg-white/5 py-2">
          <span className="block text-zinc-100">00</span>
          Batter
        </div>
        <div className="rounded bg-white/5 py-2">
          <span className="block text-zinc-100">HD</span>
          Stream
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-100">
        Grandma joined from Boise · scorekeeper synced 12s ago
      </div>
    </div>
  );
}

export default function Landing() {
  const [teams, setTeams] = useState<PublicTeamsResponse['teams']>();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.listTeamsPublic();
        if (!cancelled) setTeams(data.teams);
      } catch {
        // Landing should stay usable even if the public team list is temporarily down.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadingTeams = teams === undefined;
  const featuredTeam = teams?.[0];

  return (
    <div className="space-y-10">
      <section className="relative isolate min-h-[560px] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(16,185,129,0.34),transparent_34%),radial-gradient(circle_at_85%_22%,rgba(14,165,233,0.24),transparent_30%),linear-gradient(115deg,rgba(16,185,129,0.16),rgba(24,24,27,0.22)_42%,rgba(14,116,144,0.18)),linear-gradient(90deg,rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[size:auto,auto,auto,72px_72px,72px_72px]" />
        <div className="absolute inset-x-0 bottom-0 h-36 bg-linear-to-t from-zinc-950 to-transparent" />
        <div className="relative max-w-3xl px-6 pt-10 pb-80 sm:px-10 sm:py-14 lg:px-14">
          <Text className="text-sm font-medium uppercase tracking-[0.18em] text-emerald-300">
            Live youth baseball without the chaos
          </Text>
          <h1 className="mt-4 max-w-2xl text-4xl/none font-semibold tracking-tight text-white sm:text-6xl/none">
            The game-day hub for everyone who cannot be at the field.
          </h1>
          <Text className="mt-5 max-w-2xl text-base text-zinc-200 sm:text-lg">
            One link for live video, score overlays, approved family access, and replays that line up with every inning-changing moment.
          </Text>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button href={featuredTeam ? `/teams/${featuredTeam.teamId}` : '/teams'} color="emerald" className="w-full sm:w-auto">
              {featuredTeam ? 'Open RA Legends' : 'Watch a team'}
            </Button>
            <Button href="/login" outline className="w-full sm:w-auto">
              Parent / scorekeeper sign in
            </Button>
          </div>
          <div className="mt-8 grid max-w-xl grid-cols-3 gap-3 text-center text-xs text-zinc-400">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <div className="text-lg font-semibold text-white">30s</div>
              live status checks
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <div className="text-lg font-semibold text-white">1 tap</div>
              fan approval
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <div className="text-lg font-semibold text-white">HD</div>
              saved replays
            </div>
          </div>
        </div>
        <BroadcastPreview />
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {[
          ['For families', 'Follow a team once, then watch private lives and replays from anywhere.'],
          ['For scorekeepers', 'A phone-first cockpit keeps count, outs, bases, and mis-tap recovery close to your thumb.'],
          ['For coaches', 'Every game becomes an easy-to-find replay with the score moments lined up.'],
        ].map(([title, body]) => (
          <div key={title} className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-5 shadow-lg shadow-zinc-950/20">
            <div className="text-sm font-medium text-zinc-100">{title}</div>
            <Text className="mt-2 text-sm">{body}</Text>
          </div>
        ))}
      </section>

      <div className="rounded-2xl border border-emerald-500/20 bg-linear-to-r from-emerald-950/40 to-zinc-900 p-5 sm:flex sm:items-center sm:justify-between">
        {loadingTeams ? (
          <>
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
            <Skeleton className="mt-4 h-9 w-32 sm:mt-0" />
          </>
        ) : (
          <>
            <div>
              <Heading level={2} className="text-lg">
                {featuredTeam ? featuredTeam.name : 'Find your team'}
              </Heading>
              <Text className="mt-1 text-sm">
                {featuredTeam?.season
                  ? `${featuredTeam.season} is ready for followers.`
                  : 'Public teams appear here as they are added.'}
              </Text>
            </div>
            <Button
              href={featuredTeam ? `/teams/${featuredTeam.teamId}` : '/teams'}
              color="dark/zinc"
              className="mt-4 w-full sm:mt-0 sm:w-auto"
            >
              {featuredTeam ? 'Open team' : 'Browse teams'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
